import { useState, useEffect, useCallback } from 'react'
import {
  Comment,
  CommentFilters,
  CreateCommentData,
  UpdateCommentData
} from '../types/comments'
import {
  getRouteComments,
  getPinnedComments,
  getGeneralComments,
  getCommentReplies,
  createComment,
  updateComment,
  deleteComment,
  confirmComment,
  disputeComment,
  markCommentHelpful,
  unmarkCommentHelpful
} from '../services/firebase/comments'
import { RouteData } from '@cya-routes/shared'

interface UseRouteCommentsProps {
  routeId: string
  route?: RouteData
  userId?: string
  userDisplayName?: string
  userPhotoURL?: string
}

interface UseRouteCommentsReturn {
  // Data
  comments: Comment[]
  pinnedComments: Comment[]
  generalComments: Comment[]
  loading: boolean
  error: string | null
  
  // Actions
  addComment: (commentData: CreateCommentData) => Promise<string>
  editComment: (commentId: string, updates: UpdateCommentData) => Promise<void>
  removeComment: (commentId: string) => Promise<void>
  confirmCommentAccuracy: (commentId: string) => Promise<void>
  disputeCommentAccuracy: (commentId: string) => Promise<void>
  toggleCommentHelpful: (commentId: string, isHelpful: boolean) => Promise<void>
  getReplies: (parentId: string) => Promise<Comment[]>
  
  // Utilities
  refreshComments: () => Promise<void>
  applyFilters: (filters: CommentFilters) => void
}

export function useRouteComments({
  routeId,
  route,
  userId,
  userDisplayName,
  userPhotoURL
}: UseRouteCommentsProps): UseRouteCommentsReturn {
  const [comments, setComments] = useState<Comment[]>([])
  const [pinnedComments, setPinnedComments] = useState<Comment[]>([])
  const [generalComments, setGeneralComments] = useState<Comment[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState<CommentFilters | undefined>()

  // Load comments
  const loadComments = useCallback(async () => {
    if (!routeId) return

    setLoading(true)
    setError(null)

    try {
      // Load all comments, pinned comments, and general comments in parallel
      const [allComments, pinned, general] = await Promise.all([
        getRouteComments(routeId, filters),
        getPinnedComments(routeId),
        getGeneralComments(routeId)
      ])

      console.log('💬 Loaded comments:', {
        all: allComments.length,
        pinned: pinned.length,
        general: general.length,
        firstPinned: pinned[0]
      })
      
      setComments(allComments)
      setPinnedComments(pinned)
      setGeneralComments(general)
    } catch (err) {
      console.error('Error loading comments:', err)
      setError('Failed to load comments')
    } finally {
      setLoading(false)
    }
  }, [routeId, filters])

  // Load comments when routeId or filters change
  useEffect(() => {
    loadComments()
  }, [loadComments])

  // Add comment
  const addComment = useCallback(async (commentData: CreateCommentData): Promise<string> => {
    if (!userId || !userDisplayName) {
      throw new Error('User must be authenticated to add comments')
    }

    try {
      const commentId = await createComment(
        commentData,
        userId,
        userDisplayName,
        userPhotoURL,
        route
      )

      // Refresh comments to show the new one
      await loadComments()
      
      return commentId
    } catch (err) {
      console.error('Error adding comment:', err)
      throw new Error('Failed to add comment')
    }
  }, [userId, userDisplayName, userPhotoURL, route, loadComments])

  // Edit comment
  const editComment = useCallback(async (commentId: string, updates: UpdateCommentData): Promise<void> => {
    try {
      await updateComment(commentId, updates)
      
      // Update local state optimistically
      const updateCommentInState = (prevComments: Comment[]) =>
        prevComments.map(comment =>
          comment.id === commentId
            ? { ...comment, ...updates, updatedAt: new Date() as any }
            : comment
        )

      setComments(updateCommentInState)
      setPinnedComments(updateCommentInState)
      setGeneralComments(updateCommentInState)
    } catch (err) {
      console.error('Error updating comment:', err)
      throw new Error('Failed to update comment')
    }
  }, [])

  // Remove comment
  const removeComment = useCallback(async (commentId: string): Promise<void> => {
    try {
      await deleteComment(commentId)
      
      // Remove from local state
      const removeFromState = (prevComments: Comment[]) =>
        prevComments.filter(comment => comment.id !== commentId)

      setComments(removeFromState)
      setPinnedComments(removeFromState)
      setGeneralComments(removeFromState)
    } catch (err) {
      console.error('Error deleting comment:', err)
      throw new Error('Failed to delete comment')
    }
  }, [])

  // Confirm comment accuracy
  const confirmCommentAccuracy = useCallback(async (commentId: string): Promise<void> => {
    if (!userId) return

    try {
      await confirmComment(commentId, userId)
      
      // Update local state
      const updateConfirmations = (prevComments: Comment[]) =>
        prevComments.map(comment => {
          if (comment.id === commentId) {
            const confirmations = comment.confirmations || []
            const disputes = comment.disputes || []
            return {
              ...comment,
              confirmations: confirmations.includes(userId) 
                ? confirmations 
                : [...confirmations, userId],
              disputes: disputes.filter(id => id !== userId)
            }
          }
          return comment
        })

      setComments(updateConfirmations)
      setPinnedComments(updateConfirmations)
      setGeneralComments(updateConfirmations)
    } catch (err) {
      console.error('Error confirming comment:', err)
      throw new Error('Failed to confirm comment')
    }
  }, [userId])

  // Dispute comment accuracy
  const disputeCommentAccuracy = useCallback(async (commentId: string): Promise<void> => {
    if (!userId) return

    try {
      await disputeComment(commentId, userId)
      
      // Update local state
      const updateDisputes = (prevComments: Comment[]) =>
        prevComments.map(comment => {
          if (comment.id === commentId) {
            const confirmations = comment.confirmations || []
            const disputes = comment.disputes || []
            return {
              ...comment,
              disputes: disputes.includes(userId) 
                ? disputes 
                : [...disputes, userId],
              confirmations: confirmations.filter(id => id !== userId)
            }
          }
          return comment
        })

      setComments(updateDisputes)
      setPinnedComments(updateDisputes)
      setGeneralComments(updateDisputes)
    } catch (err) {
      console.error('Error disputing comment:', err)
      throw new Error('Failed to dispute comment')
    }
  }, [userId])

  // Toggle comment helpful status
  const toggleCommentHelpful = useCallback(async (commentId: string, isHelpful: boolean): Promise<void> => {
    if (!userId) return

    try {
      if (isHelpful) {
        await markCommentHelpful(commentId, userId)
      } else {
        await unmarkCommentHelpful(commentId, userId)
      }
      
      // Update local state
      const updateHelpful = (prevComments: Comment[]) =>
        prevComments.map(comment => {
          if (comment.id === commentId) {
            const helpful = comment.helpful || []
            return {
              ...comment,
              helpful: isHelpful
                ? helpful.includes(userId) ? helpful : [...helpful, userId]
                : helpful.filter(id => id !== userId)
            }
          }
          return comment
        })

      setComments(updateHelpful)
      setPinnedComments(updateHelpful)
      setGeneralComments(updateHelpful)
    } catch (err) {
      console.error('Error toggling comment helpful status:', err)
      throw new Error('Failed to update comment helpful status')
    }
  }, [userId])

  // Get replies to a comment
  const getReplies = useCallback(async (parentId: string): Promise<Comment[]> => {
    try {
      return await getCommentReplies(parentId)
    } catch (err) {
      console.error('Error loading replies:', err)
      throw new Error('Failed to load replies')
    }
  }, [])

  // Refresh comments
  const refreshComments = useCallback(async (): Promise<void> => {
    await loadComments()
  }, [loadComments])

  // Apply filters
  const applyFilters = useCallback((newFilters: CommentFilters): void => {
    setFilters(newFilters)
  }, [])

  return {
    // Data
    comments,
    pinnedComments,
    generalComments,
    loading,
    error,
    
    // Actions
    addComment,
    editComment,
    removeComment,
    confirmCommentAccuracy,
    disputeCommentAccuracy,
    toggleCommentHelpful,
    getReplies,
    
    // Utilities
    refreshComments,
    applyFilters
  }
}